/**
 * NativeFlowPreview — high-fidelity WhatsApp Flow screen preview
 *
 * Renders each screen as a WhatsApp-style bottom sheet showing the actual
 * native components: RadioButtonsGroup, DatePicker, TextBody, TextHeading,
 * TextInput, TextArea, CheckboxGroup, Dropdown, Footer, etc.
 *
 * Matches the real WhatsApp Flows UI: bottom sheet with grip handle, close
 * button, title bar, form components, and CTA button at the bottom.
 */

import { useTranslation } from 'react-i18next';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleDot,
  CheckCircle2,
  Square,
  SquareCheck,
  Type,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// -- Types matching the Flow JSON v7.0 structure --

interface FlowComponent {
  type: string;
  text?: string;
  label?: string;
  name?: string;
  'input-type'?: string;
  'helper-text'?: string;
  placeholder?: string;
  'data-source'?: Array<{ id: string; title: string; description?: string }>;
  'on-click-action'?: { name: string; next?: { name: string } };
  'on-select-action'?: { name: string; next?: { name: string } };
  'min-date'?: string;
  'max-date'?: string;
  required?: boolean;
  visible?: boolean;
  enabled?: boolean;
  src?: string;
  width?: number;
  height?: number;
}

interface FlowScreen {
  id: string;
  title: string;
  terminal?: boolean;
  success?: boolean;
  layout?: {
    type: string;
    children?: Array<{
      type: string;
      name?: string;
      children?: FlowComponent[];
    }>;
  };
}

interface FlowJson {
  version: string;
  screens: FlowScreen[];
  data_api_version?: string;
  routing_model?: Record<string, string[]>;
}

// WhatsApp color constants
const WA = {
  green: '#00a884',
  greenDark: '#008069',
  textPrimary: '#111b21',
  textSecondary: '#667781',
  border: '#e9edef',
  inputBg: '#f0f2f5',
  white: '#ffffff',
  sheetBg: '#ffffff',
  overlay: 'rgba(11,20,26,0.4)',
} as const;

// -- Component renderers (WhatsApp-native styling) --

function RenderTextHeading({ text }: { text: string }) {
  return (
    <p style={{ fontSize: 15, fontWeight: 600, color: WA.textPrimary, lineHeight: '20px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {text}
    </p>
  );
}

function RenderTextSubheading({ text }: { text: string }) {
  return (
    <p style={{ fontSize: 13, fontWeight: 600, color: WA.textPrimary, lineHeight: '18px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {text}
    </p>
  );
}

function RenderTextBody({ text }: { text: string }) {
  // Replace ${data.xxx} with styled inline badges
  const parts = text.split(/(\$\{[^}]+\})/g);
  return (
    <p style={{ fontSize: 13, color: WA.textSecondary, lineHeight: '18px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {parts.map((part, i) =>
        part.startsWith('${') ? (
          <span key={i} style={{ backgroundColor: '#e7f8f0', color: WA.greenDark, borderRadius: 3, padding: '1px 4px', fontSize: 11, fontFamily: 'monospace' }}>
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </p>
  );
}

function RenderTextCaption({ text }: { text: string }) {
  return (
    <p style={{ fontSize: 11, color: WA.textSecondary, lineHeight: '15px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {text}
    </p>
  );
}

function RenderRadioButtonsGroup({ component }: { component: FlowComponent }) {
  const { t } = useTranslation();
  const items = component['data-source'] || [];
  const displayItems = items.length > 0 ? items.slice(0, 5) : [
    { id: '1', title: `${t('nativeFlows.preview.option')} 1` },
    { id: '2', title: `${t('nativeFlows.preview.option')} 2` },
    { id: '3', title: `${t('nativeFlows.preview.option')} 3` },
  ];
  const hasMore = items.length > 5;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {component.label && (
        <p style={{ fontSize: 12, fontWeight: 500, color: WA.textPrimary, marginBottom: 2 }}>{component.label}</p>
      )}
      {displayItems.map((item, i) => (
        <div
          key={item.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            border: `1.5px solid ${i === 0 ? WA.green : WA.border}`,
            borderRadius: 10,
            padding: '10px 12px',
            backgroundColor: i === 0 ? 'rgba(0,168,132,0.04)' : WA.white,
            cursor: 'default',
          }}
        >
          {i === 0 ? (
            <CircleDot className="shrink-0" style={{ width: 18, height: 18, color: WA.green }} />
          ) : (
            <Circle className="shrink-0" style={{ width: 18, height: 18, color: '#c5ccd2' }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 13, color: WA.textPrimary, fontFamily: '-apple-system, sans-serif' }} className="truncate block">
              {item.title}
            </span>
            {item.description && (
              <span style={{ fontSize: 11, color: WA.textSecondary }} className="truncate block">{item.description}</span>
            )}
          </div>
        </div>
      ))}
      {hasMore && (
        <p style={{ fontSize: 11, color: WA.textSecondary, textAlign: 'center', padding: '2px 0' }}>
          {t('nativeFlows.preview.moreOptions', { count: items.length - 5 })}
        </p>
      )}
    </div>
  );
}

function RenderCheckboxGroup({ component }: { component: FlowComponent }) {
  const items = component['data-source'] || [];
  const displayItems = items.length > 0 ? items.slice(0, 4) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {component.label && (
        <p style={{ fontSize: 12, fontWeight: 500, color: WA.textPrimary, marginBottom: 2 }}>{component.label}</p>
      )}
      {displayItems.map((item, i) => (
        <div
          key={item.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            border: `1.5px solid ${i === 0 ? WA.green : WA.border}`,
            borderRadius: 10,
            padding: '10px 12px',
            backgroundColor: i === 0 ? 'rgba(0,168,132,0.04)' : WA.white,
          }}
        >
          {i === 0 ? (
            <SquareCheck className="shrink-0" style={{ width: 18, height: 18, color: WA.green, borderRadius: 3 }} />
          ) : (
            <Square className="shrink-0" style={{ width: 18, height: 18, color: '#c5ccd2', borderRadius: 3 }} />
          )}
          <span style={{ fontSize: 13, color: WA.textPrimary, fontFamily: '-apple-system, sans-serif' }} className="truncate">
            {item.title}
          </span>
        </div>
      ))}
    </div>
  );
}

function RenderDatePicker({ component }: { component: FlowComponent }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {component.label && (
        <p style={{ fontSize: 12, fontWeight: 500, color: WA.textPrimary }}>{component.label}</p>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: `1.5px solid ${WA.border}`,
          borderRadius: 10,
          padding: '10px 12px',
          backgroundColor: WA.white,
        }}
      >
        <Calendar className="shrink-0" style={{ width: 18, height: 18, color: WA.green }} />
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 13, color: WA.textSecondary, fontFamily: '-apple-system, sans-serif' }}>
            {component.label || t('nativeFlows.preview.selectDate')}
          </p>
          {(component['min-date'] || component['max-date']) && (
            <p style={{ fontSize: 10, color: '#a0aab1', marginTop: 2 }}>
              {component['min-date']} &ndash; {component['max-date']}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function RenderTextInput({ component }: { component: FlowComponent }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {component.label && (
        <p style={{ fontSize: 12, fontWeight: 500, color: WA.textPrimary }}>{component.label}</p>
      )}
      <div
        style={{
          border: `1.5px solid ${WA.border}`,
          borderRadius: 10,
          padding: '10px 12px',
          backgroundColor: WA.white,
          fontSize: 13,
          color: '#a0aab1',
          fontFamily: '-apple-system, sans-serif',
        }}
      >
        {component.placeholder || component.label || component.name || ''}
      </div>
      {component['helper-text'] && (
        <p style={{ fontSize: 10, color: WA.textSecondary, paddingLeft: 4 }}>{component['helper-text']}</p>
      )}
    </div>
  );
}

function RenderTextArea({ component }: { component: FlowComponent }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {component.label && (
        <p style={{ fontSize: 12, fontWeight: 500, color: WA.textPrimary }}>{component.label}</p>
      )}
      <div
        style={{
          border: `1.5px solid ${WA.border}`,
          borderRadius: 10,
          padding: '10px 12px',
          minHeight: 56,
          backgroundColor: WA.white,
          fontSize: 13,
          color: '#a0aab1',
          fontFamily: '-apple-system, sans-serif',
        }}
      >
        {component.placeholder || component.label || ''}
      </div>
      {component['helper-text'] && (
        <p style={{ fontSize: 10, color: WA.textSecondary, paddingLeft: 4 }}>{component['helper-text']}</p>
      )}
    </div>
  );
}

function RenderDropdown({ component }: { component: FlowComponent }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {component.label && (
        <p style={{ fontSize: 12, fontWeight: 500, color: WA.textPrimary }}>{component.label}</p>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: `1.5px solid ${WA.border}`,
          borderRadius: 10,
          padding: '10px 12px',
          backgroundColor: WA.white,
        }}
      >
        <span style={{ fontSize: 13, color: '#a0aab1', fontFamily: '-apple-system, sans-serif' }}>
          {component.label || 'Select'}
        </span>
        <ChevronDown style={{ width: 16, height: 16, color: '#a0aab1' }} />
      </div>
    </div>
  );
}

function RenderOptIn({ component }: { component: FlowComponent }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 0',
      }}
    >
      {/* Toggle switch */}
      <div style={{ width: 36, height: 20, borderRadius: 10, backgroundColor: WA.green, position: 'relative', flexShrink: 0 }}>
        <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#fff', position: 'absolute', top: 2, right: 2, boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
      </div>
      <span style={{ fontSize: 12, color: WA.textPrimary, fontFamily: '-apple-system, sans-serif' }}>
        {component.label || component.text || ''}
      </span>
    </div>
  );
}

function RenderImage({ component }: { component: FlowComponent }) {
  const h = component.height || 80;
  return (
    <div
      style={{
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: WA.inputBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: Math.min(h, 100),
      }}
    >
      {component.src ? (
        <img src={component.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#a0aab1" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
      )}
    </div>
  );
}

function RenderEmbeddedLink({ component }: { component: FlowComponent }) {
  return (
    <p style={{ fontSize: 12, color: '#027eb5', textDecoration: 'underline', fontFamily: '-apple-system, sans-serif' }}>
      {component.text || component.label || 'Link'}
    </p>
  );
}

function RenderFooter({ component }: { component: FlowComponent }) {
  const { t } = useTranslation();
  const actionName = component['on-click-action']?.name;
  const isComplete = actionName === 'complete';

  return (
    <div style={{ marginTop: 'auto', paddingTop: 8 }}>
      <div
        style={{
          width: '100%',
          borderRadius: 24,
          padding: '11px 16px',
          textAlign: 'center',
          fontSize: 14,
          fontWeight: 600,
          color: '#fff',
          backgroundColor: isComplete ? WA.greenDark : WA.green,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          cursor: 'default',
        }}
      >
        {component.label || t('nativeFlows.preview.continue')}
      </div>
      {actionName && (
        <p style={{ fontSize: 9, color: WA.textSecondary, textAlign: 'center', marginTop: 4 }}>
          {actionName === 'data_exchange' ? t('nativeFlows.preview.fetchServerData') :
           actionName === 'complete' ? t('nativeFlows.preview.submitComplete') :
           actionName === 'navigate' ? t('nativeFlows.preview.navigateNext') : actionName}
        </p>
      )}
    </div>
  );
}

function RenderComponent({ component }: { component: FlowComponent }) {
  switch (component.type) {
    case 'TextHeading':
      return <RenderTextHeading text={component.text || ''} />;
    case 'TextSubheading':
      return <RenderTextSubheading text={component.text || ''} />;
    case 'TextBody':
      return <RenderTextBody text={component.text || ''} />;
    case 'TextCaption':
      return <RenderTextCaption text={component.text || ''} />;
    case 'RadioButtonsGroup':
      return <RenderRadioButtonsGroup component={component} />;
    case 'CheckboxGroup':
      return <RenderCheckboxGroup component={component} />;
    case 'DatePicker':
      return <RenderDatePicker component={component} />;
    case 'TextInput':
      return <RenderTextInput component={component} />;
    case 'TextArea':
      return <RenderTextArea component={component} />;
    case 'Dropdown':
      return <RenderDropdown component={component} />;
    case 'OptIn':
      return <RenderOptIn component={component} />;
    case 'Image':
      return <RenderImage component={component} />;
    case 'EmbeddedLink':
      return <RenderEmbeddedLink component={component} />;
    case 'Footer':
      return <RenderFooter component={component} />;
    default:
      return (
        <div className="flex items-center gap-1.5" style={{ fontSize: 10, color: WA.textSecondary, border: `1px solid ${WA.border}`, borderRadius: 6, padding: '4px 8px' }}>
          <Type style={{ width: 12, height: 12 }} />
          {component.type}{component.label ? `: ${component.label}` : component.name ? `: ${component.name}` : ''}
        </div>
      );
  }
}

// -- Screen card styled as WhatsApp bottom sheet --

function ScreenCard({ screen, index, total }: { screen: FlowScreen; index: number; total: number }) {
  const { t } = useTranslation();
  const formChildren = screen.layout?.children?.[0]?.children || [];

  return (
    <div className="flex-shrink-0" style={{ width: 240 }}>
      <div
        style={{
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)',
          backgroundColor: WA.sheetBg,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* Grip handle (WhatsApp bottom sheet style) */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8, paddingBottom: 4 }}>
          <div style={{ width: 32, height: 4, borderRadius: 2, backgroundColor: '#c5ccd2' }} />
        </div>

        {/* Sheet header: X button + title */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '4px 12px 8px', gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: WA.inputBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <X style={{ width: 14, height: 14, color: WA.textSecondary }} />
          </div>
          <p style={{ flex: 1, textAlign: 'center', fontSize: 14, fontWeight: 600, color: WA.textPrimary, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {screen.title}
          </p>
          {/* Badge for screen index */}
          <div style={{ width: 28, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 9, color: WA.textSecondary, fontWeight: 500 }}>{index + 1}/{total}</span>
          </div>
        </div>

        {/* Separator */}
        <div style={{ height: 1, backgroundColor: WA.border, margin: '0 12px' }} />

        {/* Form body */}
        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10, minHeight: 200 }}>
          {formChildren.length > 0 ? (
            formChildren.map((comp, i) => (
              <RenderComponent key={`${screen.id}-${i}`} component={comp} />
            ))
          ) : (
            <p style={{ fontSize: 12, color: WA.textSecondary, fontStyle: 'italic', textAlign: 'center', padding: '32px 0' }}>
              {t('nativeFlows.preview.noComponents')}
            </p>
          )}
        </div>

        {/* Terminal indicator */}
        {screen.terminal && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '0 12px 8px' }}>
            <CheckCircle2 style={{ width: 12, height: 12, color: WA.green }} />
            <span style={{ fontSize: 9, color: WA.green, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {screen.success ? 'SUCCESS' : 'TERMINAL'}
            </span>
          </div>
        )}

        {/* Screen ID footer */}
        <div style={{ borderTop: `1px solid ${WA.border}`, padding: '6px 12px', backgroundColor: WA.inputBg }}>
          <p style={{ fontSize: 9, color: '#a0aab1', fontFamily: 'monospace', letterSpacing: '-0.2px' }}>{screen.id}</p>
        </div>
      </div>

      {/* Arrow connector to next screen */}
      {index < total - 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0' }}>
          <ChevronRight style={{ width: 16, height: 16, color: '#c5ccd2', transform: 'rotate(90deg)' }} />
        </div>
      )}
    </div>
  );
}

// -- Main preview component --

export function NativeFlowPreview({ flowJson }: { flowJson: FlowJson | null | undefined }) {
  const { t } = useTranslation();
  if (!flowJson || !flowJson.screens || flowJson.screens.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{t('nativeFlows.preview.title')}</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">
              v{flowJson.version}
            </Badge>
            <Badge variant="secondary" className="text-[10px]">
              {t('nativeFlows.preview.screens', { count: flowJson.screens.length })}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Horizontal scroll for screen cards */}
        <div className="flex gap-5 overflow-x-auto pb-3 -mx-1 px-1" style={{ scrollSnapType: 'x mandatory' }}>
          {flowJson.screens.map((screen, i) => (
            <ScreenCard
              key={screen.id}
              screen={screen}
              index={i}
              total={flowJson.screens.length}
            />
          ))}
        </div>

        {/* Routing info */}
        {flowJson.routing_model && (
          <div className="mt-3 pt-3 border-t">
            <p style={{ fontSize: 10, color: WA.textSecondary, fontWeight: 500, marginBottom: 4 }}>{t('nativeFlows.preview.routing')}</p>
            <div className="flex flex-wrap gap-1">
              {Object.entries(flowJson.routing_model).map(([from, to]) => (
                <span key={from} style={{ fontSize: 9, color: WA.textSecondary, fontFamily: 'monospace', backgroundColor: WA.inputBg, padding: '2px 6px', borderRadius: 4 }}>
                  {from} → {Array.isArray(to) ? to.join(', ') : to}
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
